import {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  MutableRefObject,
} from 'react';
import { ModelsMap } from 'src/components/common/ForgeViewer';
import { PropertyContext } from 'src/domain/property-operations';
import { ModelsDeepFragment } from 'src/gql/graphql';

export type HistoryDiff = Record<string, any>;

interface ProjectHistoryState {
  isHistoryDrawerOpen: boolean;
  setIsHistoryDrawerOpen: Dispatch<SetStateAction<boolean>>;
  selectedRange: [number, number];
  setSelectedRange: Dispatch<SetStateAction<[number, number]>>;
  diff: HistoryDiff | null;
  setDiff: Dispatch<SetStateAction<HistoryDiff | null>>;
  referenceState: any;
  setReferenceState: Dispatch<SetStateAction<any>>;
  eventsActiveModels: ModelsDeepFragment[] | null;
  setEventsActiveModels: Dispatch<SetStateAction<ModelsDeepFragment[] | null>>;
  tempModelOverlaysRef: MutableRefObject<ModelsMap>;
  resolvedStates: [any, any] | null;
  setResolvedStates: Dispatch<SetStateAction<[any, any] | null>>;
}

const ProjectHistoryContext = createContext<ProjectHistoryState | undefined>(
  undefined
);

export type HistoricalPropertyContext = Omit<
  PropertyContext,
  'loadedModels'
> & { loadedModels: Array<string> };

export const ProjectHistoryProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  // Default to [0, 2] to show diff between current version and latest action
  const [selectedRange, setSelectedRange] = useState<[number, number]>([0, 2]);
  const [diff, setDiff] = useState<HistoryDiff | null>(null);
  const [resolvedStates, setResolvedStates] = useState<[any, any] | null>(null);
  const [referenceState, setReferenceState] =
    useState<HistoricalPropertyContext | null>(null);
  const [eventsActiveModels, setEventsActiveModels] = useState<
    ModelsDeepFragment[] | null
  >(null);
  // contains the temporarily loaded models
  const tempModelOverlaysRef = useRef<ModelsMap>({});

  useEffect(() => {
    console.log('isHistoryDrawerOpen', isHistoryDrawerOpen);
  }, [isHistoryDrawerOpen]);

  useEffect(() => {
    console.log('selectedRange', selectedRange);
  }, [selectedRange]);

  useEffect(() => {
    console.log('diff', diff);
    console.log('referenceState', referenceState);
  }, [diff, referenceState]);

  const value = useMemo(
    () => ({
      isHistoryDrawerOpen,
      setIsHistoryDrawerOpen,
      selectedRange,
      setSelectedRange,
      diff,
      setDiff,
      referenceState,
      setReferenceState,
      eventsActiveModels,
      setEventsActiveModels,
      tempModelOverlaysRef,
      resolvedStates,
      setResolvedStates,
    }),
    [
      isHistoryDrawerOpen,
      selectedRange,
      diff,
      referenceState,
      eventsActiveModels,
      resolvedStates,
    ]
  );

  return (
    <ProjectHistoryContext.Provider value={value}>
      {children}
    </ProjectHistoryContext.Provider>
  );
};

export const useProjectHistory = () => {
  const context = useContext(ProjectHistoryContext);
  if (context === undefined) {
    throw new Error(
      'useProjectHistory must be used within a ProjectHistoryProvider'
    );
  }
  return context;
};
